import React, { useEffect, useState } from 'react';

import Divider from '@mui/material/Divider';
import { Controller, useForm } from 'react-hook-form';
import { Link, useHistory } from 'react-router-dom';
import entraIcon from 'styles/assets/svg/entra-icon.svg';
import oktaIcon from 'styles/assets/svg/okta-icon.svg';

import { useQueryState } from 'hooks/useQueryState';

import { useLoginStore } from 'store/login.store';

import { useMutationGetMFACode } from 'api/mutations/getMFACode.mutation';
import { useMutationLogin } from 'api/mutations/login.mutation';
import { useMutationValidateLogin } from 'api/mutations/validateLogin.mutation';
import { useQueryCheckMFAEnabled } from 'api/queries/checkMFAEnabled.query';
import { useQueryGetAccessToken } from 'api/queries/getAccessToken.query';
import { useQueryGetUserDetail } from 'api/queries/getUserDetail.query';
import { useQueryServerTime } from 'api/queries/serverTime.query';

import { PasswordInput } from 'components/common/PasswordInput/PasswordInput';

import classes from 'components/App/AuthPublicPages/NewDesign/Login/Login.module.scss';
import { GENERIC_ERROR, encrypt } from 'components/App/AuthPublicPages/NewDesign/Login/Login.utils';
import { Layout } from 'components/App/AuthPublicPages/components/Layout/Layout';
import { useLoginHook } from 'components/App/AuthPublicPages/hooks/loginHook';
import { Button } from 'components/DesignSystem/Button';
import { TextInput } from 'components/DesignSystem/TextInput';
import { CustomizedFormErrorMessage } from 'components/common';

export interface ILoginForm {
    password: string;
    username: string;
    mfaCode: null;
}

export const Login = () => {
    const url = new URL(document.URL);
    const history = useHistory();
    const form = useForm<ILoginForm>({
        defaultValues: {
            password: '',
            username: '',
            mfaCode: null,
        },
    });
    const { update } = useLoginStore();
    const [email, setEmail] = useState<string | undefined>(undefined);
    const [password, setPassword] = useState<string | undefined>(undefined);
    const [emailAfterMfa, setEmailAfterMfa] = useState<string | null>(null);
    const [error, setError] = useState<string | undefined>(undefined);
    const { data: serverTime } = useQueryServerTime();
    const { mutate: loginMutation, isLoading: isLoadingLogin } = useMutationLogin();
    const { mutate: validateLoginMutation, isLoading: isLoadingValidateLogin } = useMutationValidateLogin();
    const { mutate: getMFACodeMutation, isLoading: isLoadingGetMFACode } = useMutationGetMFACode(); // send mfa code to user by email
    const [xyz] = useQueryState('xyz');
    const [ssoType] = useQueryState('ssoType');
    const { data: activeDirectoryUser, isLoading: isLoadingActiveDirectoryUser } = useQueryGetAccessToken({
        uuid: xyz,
        ssoType,
    });
    const { data: user, isLoading: isLoadingUserDetail } = useQueryGetUserDetail({
        email: emailAfterMfa || activeDirectoryUser?.username,
    });

    const { data: mfa } = useQueryCheckMFAEnabled({ email, password });

    useLoginHook({ user, isSwitchUser: false });

    useEffect(() => {
        if (mfa) {
            const time = serverTime?.time as number;
            const payload = time !== 0 ? { encrypt: encrypt(JSON.stringify(form.watch()), time), time } : form.watch();
            if (mfa.mfaType === 'off') {
                loginMutation(payload, {
                    onSuccess: (response) => {
                        if (response.email) {
                            setEmailAfterMfa(response.email);
                            setError(undefined);
                        } else {
                            setError(GENERIC_ERROR);
                        }
                    },
                    onError: () => {
                        setError(GENERIC_ERROR);
                    },
                });
            } else {
                validateLoginMutation(payload, {
                    onSuccess: (response) => {
                        if (response.valid) {
                            if (mfa.mfaType === 'email') {
                                update({ username: form.watch('username'), password: form.watch('password') });
                                getMFACodeMutation(
                                    { email: form.watch('username') },
                                    {
                                        onSuccess: () => {
                                            history.push(`/mfa?type=${mfa.mfaType}`);
                                        },
                                        onError: () => {
                                            setError(GENERIC_ERROR);
                                        },
                                    },
                                );
                            }
                            if (['microsoft', 'google'].includes(mfa.mfaType)) {
                                update({ username: form.watch('username'), password: form.watch('password') });
                                history.push(
                                    mfa.mfaInit ? `/mfaSignUp?type=${mfa.mfaType}` : `/mfa?type=${mfa.mfaType}`,
                                );
                            }
                            setError(undefined);
                        } else {
                            setError(GENERIC_ERROR);
                        }
                    },
                    onError: () => {
                        setError(GENERIC_ERROR);
                    },
                });
            }
        }
    }, [mfa, serverTime]);

    const handleLogin = (details: ILoginForm): void => {
        setEmail(details.username);
        setPassword(details.password);
    };

    const handleSsoLogin = (sso) => {
        window.location.href = window['ENV_VARIABLES'][sso].replace('$$_IP$$', url.hostname);
    };

    const showActiveDirectoryButton = window['ENV_VARIABLES']['REACT_APP_SHOW_AAD_SSO'].indexOf(url.hostname) > -1;
    const showOktaButton = window['ENV_VARIABLES']['REACT_APP_SHOW_OKTA_SSO'].indexOf(url.hostname) > -1;
    const showOrDivider = showActiveDirectoryButton || showOktaButton;
    const isLoading =
        isLoadingActiveDirectoryUser ||
        isLoadingUserDetail ||
        isLoadingLogin ||
        isLoadingGetMFACode ||
        isLoadingValidateLogin;

    return (
        <Layout>
            <form className={classes.container} onSubmit={form.handleSubmit(handleLogin)}>
                <div className={classes.title}>Sign in to your account</div>
                <div className={classes.formRows}>
                    <div className={classes.row}>
                        <span className={classes.label}>Email</span>
                        <Controller
                            render={({ field }) => <TextInput {...field} clearable={false} />}
                            name={'username'}
                            control={form.control}
                        />
                    </div>
                    <div className={classes.row}>
                        <span className={classes.label}>Password</span>
                        <Controller
                            render={({ field }) => <PasswordInput {...field} />}
                            name={'password'}
                            control={form.control}
                        />
                    </div>
                    {error && (
                        <div className={classes.row}>
                            <CustomizedFormErrorMessage text={error} />
                        </div>
                    )}
                    <div className={classes.row}>
                        <Button variant={'primary'} isLoading={isLoading} type={'submit'}>
                            Sign In
                        </Button>
                    </div>
                    <div className={classes.row}>
                        <Button component={Link} to={'/forgot-password'} variant={'tertiary'}>
                            Forgot Password?
                        </Button>
                    </div>
                    {showOrDivider && <Divider className={classes.divider}>OR</Divider>}
                    {showOktaButton && (
                        <div className={classes.row}>
                            <Button
                                component={Link}
                                onClick={() => handleSsoLogin('REACT_APP_OKTA_URL')}
                                variant={'secondary'}
                                startIcon={<img src={oktaIcon} alt={'Okta SSO'} />}
                            >
                                Sign in with Okta SSO
                            </Button>
                        </div>
                    )}
                    {showActiveDirectoryButton && (
                        <div className={classes.row}>
                            <Button
                                component={Link}
                                onClick={() => handleSsoLogin('REACT_APP_ACTIVE_DIRECTORY_URL')}
                                variant={'secondary'}
                                startIcon={<img src={entraIcon} alt={'Entra SSO'} />}
                            >
                                Sign in with Entra SSO
                            </Button>
                        </div>
                    )}
                </div>
            </form>
        </Layout>
    );
};
